"use client";

import React, { useEffect, useMemo, useState } from 'react';
import { ComponentNode } from '../types/template';
import { convertStylesToInline, getCustomThemeGridStyle } from '../utils/styleUtils';
import { ApiRequestConfig, DataFetcher, fetchRawDataForRenderRequest } from '../utils/DataFetcher';
import { normalizeTemplateItem } from '../utils/normalizeTemplateItem';
import { buildRequestConfig, getRouteTemplateVariables } from '../utils/templateVariables';
import ComponentRenderer from './ComponentRenderer';
import EmptyStateComponent from './EmptyStateComponent';

interface CategorySectionComponentProps {
    component: ComponentNode;
    context?: {
        companyId?: string;
        domain?: string;
        params?: Record<string, any>;
        searchParams?: Record<string, any>;
    };
}

type CategoryItem = {
    category?: {
        url?: string;
        name?: string;
        child?: unknown;
        [key: string]: any;
    };
    category_name?: string;
    [key: string]: any;
};

/**
 * `parent_categories_with_products` returns `url` as a bare slug
 * ("computer-electronics") rather than a routable path, so a card bound to it
 * would link to "/computer-electronics" — a 404. Every other slug_type already
 * returns "category/<slug>".
 */
const routeCategoryUrl = (slugType: string | undefined, category: any) => {
    if (slugType !== 'parent_categories_with_products') return category;
    const slug = String(category?.slug || category?.url || '').replace(/^\/+/, '');
    return slug ? { ...category, url: `category/${slug}` } : category;
};

const normalizeCategoryResponse = (response: any): CategoryItem[] => {
    const normalized = DataFetcher.normalizeCollectionResponse(response);
    const slugType = response?.data?.slug_type;

    return normalized.map((item: any) => {
        const category = routeCategoryUrl(
            slugType,
            item?.category && typeof item.category === 'object' ? item.category : item,
        );

        return { ...item, category } as CategoryItem;
    });
};

const sortCategories = (items: CategoryItem[]) => {
    return [...items].sort((a, b) => {
        const nameA = String(a?.category?.name || a?.category_name || '').toLowerCase();
        const nameB = String(b?.category?.name || b?.category_name || '').toLowerCase();
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
};

const cloneComponentTree = (node: ComponentNode): ComponentNode => {
    return JSON.parse(JSON.stringify(node));
};

const hideSubcategoryPanel = (node: ComponentNode): ComponentNode => {
    const hiddenNode = { ...node } as ComponentNode;
    if (hiddenNode.customClassName === 'cb-category-sub-panel' || hiddenNode.id?.includes('subcategory-scroll')) {
        hiddenNode.styles = {
            ...hiddenNode.styles,
            display: 'none',
        };
        return hiddenNode;
    }

    if (Array.isArray(hiddenNode.components)) {
        hiddenNode.components = hiddenNode.components.map(hideSubcategoryPanel);
    }

    return hiddenNode;
};

const CategorySectionComponent: React.FC<CategorySectionComponentProps> = ({ component, context }) => {
    const [categories, setCategories] = useState<CategoryItem[]>([]);
    const [openItemId, setOpenItemId] = useState<string | number | null>(null);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const inlineStyles = convertStylesToInline({ position: 'relative', ...component.styles });
    const gridStyle = {
        ...getCustomThemeGridStyle(component.gridCols, component.gridGap),
        alignItems: 'start' as const,
    };

    const headerComponents = component.components?.filter((child) => !child.repeated) ?? [];
    const repeatedBlock = component.components?.find((child) => child.repeated);
    const requestKey = JSON.stringify([
        component.apiConfig,
        component.apiEndpoint,
        context?.companyId,
        context?.domain,
        context?.params,
        context?.searchParams,
    ]);

    useEffect(() => {
        const fetchCategories = async () => {
            setLoading(true);
            setErrorMessage(null);

            try {
                const templateVars = getRouteTemplateVariables(context);
                const requestConfig: ApiRequestConfig | undefined = component.apiConfig
                    ? buildRequestConfig(component.apiConfig, templateVars)
                    : component.apiEndpoint
                        ? { apiEndpoint: component.apiEndpoint, method: 'POST', body: {} }
                        : undefined;

                if (!requestConfig) {
                    setCategories([]);
                    setLoading(false);
                    return;
                }

                const response = await fetchRawDataForRenderRequest(requestConfig, {
                    companyId: context?.companyId,
                    domain: context?.domain,
                });

                const resolvedCategories = normalizeCategoryResponse(response);
                setCategories(resolvedCategories);
            } catch (error) {
                setErrorMessage('Unable to load categories.');
                console.error('CategorySectionComponent error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
        // requestKey captures the serialized API config and route values.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const sortedCategories = useMemo(() => {
        const sorted = sortCategories(categories);
        return component.totalCards ? sorted.slice(0, component.totalCards) : sorted;
    }, [categories, component.totalCards]);

    if (!loading && !errorMessage && sortedCategories.length === 0) {
        return (
            <section style={inlineStyles} className={component.customClassName}>
                <EmptyStateComponent kind="categories" />
            </section>
        );
    }

    return (
        <section
            style={inlineStyles}
            className={component.customClassName}
            onClick={(e) => {
                if ((e.target as HTMLElement).closest('.cb-category-sheet-overlay')) {
                    setOpenItemId(null);
                }
            }}
        >
            {headerComponents.map((child) => (
                <ComponentRenderer
                    key={child.id}
                    component={child}
                    context={context}
                />
            ))}

            {errorMessage && (
                <div style={{ color: '#dc2626', marginTop: '16px' }}>{errorMessage}</div>
            )}

            {loading ? (
                <div style={{ padding: '24px 0', textAlign: 'center', color: '#475569' }}>
                    Loading categories...
                </div>
            ) : (
                <div style={gridStyle} className="cb-responsive-grid">
                    {repeatedBlock && sortedCategories.map((item, index) => {
                        const itemId = item?.category?.url || item?.category?.name || index;
                        const isOpen = openItemId === itemId;
                        const block = cloneComponentTree(repeatedBlock);
                        const renderedBlock = isOpen
                            ? { ...block, customClassName: [block.customClassName, 'cb-category-open'].filter(Boolean).join(' ') }
                            : hideSubcategoryPanel(block);
                        const normalizedCategoryData = normalizeTemplateItem({
                            ...item?.category,
                            child: item?.category?.child,
                        });
                        const hasChildren = Array.isArray(item?.category?.child) && item.category.child.length > 0;
                        const isTilesLayout = component.layout === 'tiles';

                        return (
                            <div
                                key={`category-block-${itemId}-${index}`}
                                style={{ width: '100%', alignSelf: 'start', position: 'relative' }}
                            >
                                <ComponentRenderer
                                    component={renderedBlock}
                                    data={normalizedCategoryData}
                                    context={context}
                                />
                                {hasChildren && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenItemId((cur) => (cur === itemId ? null : itemId));
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: '30px',
                                            right: '12px',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            color: '#64748b',
                                            lineHeight: 1,
                                        }}
                                        aria-label="Open subcategories"
                                    >
                                        {isTilesLayout ? (
                                            // Chevron for tiles layout
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                                style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        ) : (
                                            // Diagonal arrow for columns layout
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="7" y1="17" x2="17" y2="7" />
                                                <polyline points="7 7 17 7 17 17" />
                                            </svg>
                                        )}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
};

export default CategorySectionComponent;
